import * as XLSX from "xlsx";
import * as XLSXStyle from "xlsx-js-style";
import { saveAs } from "file-saver";
import { TableColumn } from "@/components/ui/Table";

/**
 * 엑셀 다운로드 옵션
 */
export interface ExcelDownloadOptions {
  /** 파일명 (확장자 제외) */
  fileName?: string;
  /** 시트명 */
  sheetName?: string;
  /** 그룹 헤더 포함 여부 */
  includeGroupHeader?: boolean;
  /** 날짜 포맷 (파일명에 추가) */
  includeDateInFileName?: boolean;
}

/**
 * 컬럼 정의에서 엑셀 헤더 추출
 * 그룹 헤더가 있는 경우 "그룹명 - 헤더명" 형태로 반환
 */
const getExcelHeader = (column: TableColumn, includeGroup: boolean): string => {
  if (includeGroup && column.group) {
    return `${column.group} - ${column.header}`;
  }
  return column.header;
};

/**
 * 데이터 값을 엑셀에 적합한 형태로 변환
 * render 함수가 있는 경우 해당 함수의 결과에서 텍스트 추출
 */
const getExcelValue = (
  value: any,
  row: any,
  index: number,
  column: TableColumn
): string | number => {
  // render 함수가 있는 경우
  if (column.render) {
    const rendered = column.render(value, row, index);
    // React 엘리먼트인 경우 원본 값 사용
    if (typeof rendered === "object" && rendered !== null) {
      // 숫자 포맷팅된 문자열인 경우 쉼표 제거하여 숫자로 변환 시도
      if (typeof value === "number") {
        return value;
      }
      return String(value ?? "");
    }
    return rendered as string | number;
  }

  // 값이 없는 경우
  if (value === null || value === undefined) {
    return "";
  }

  // 숫자인 경우 그대로 반환
  if (typeof value === "number") {
    return value;
  }

  return String(value);
};

/**
 * 오늘 날짜를 YYYYMMDD 형식으로 반환
 */
const getDateString = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}${month}${day}`;
};

/**
 * 테이블 데이터를 엑셀 파일로 다운로드
 *
 * @param data - 테이블 데이터 배열
 * @param columns - 테이블 컬럼 정의 배열
 * @param options - 다운로드 옵션
 *
 * @example
 * // 기본 사용
 * downloadExcel(tableData, columns, { fileName: "매출현황" });
 *
 * @example
 * // 그룹 헤더 포함
 * downloadExcel(tableData, columns, {
 *   fileName: "거래처별매출",
 *   includeGroupHeader: true
 * });
 */
export const downloadExcel = <T extends Record<string, any>>(
  data: T[],
  columns: TableColumn[],
  options: ExcelDownloadOptions = {}
): void => {
  const {
    fileName = "export",
    sheetName = "Sheet1",
    includeGroupHeader = true,
    includeDateInFileName = true,
  } = options;

  // 내부 메타데이터 컬럼 제외 (GroupTable의 __rowType 등)
  const exportColumns = columns.filter(
    col => !col.key.startsWith("__") && col.key !== "no"
  );

  // 헤더 생성
  const headers = exportColumns.map(col =>
    getExcelHeader(col, includeGroupHeader)
  );

  // 데이터 행 생성
  const rows = data
    .filter(row => {
      // GroupTable의 합계행 제외 (선택적)
      if ("__rowType" in row && row.__rowType === "summary") {
        return false;
      }
      return true;
    })
    .map((row, rowIndex) =>
      exportColumns.map(col =>
        getExcelValue(row[col.key], row, rowIndex, col)
      )
    );

  // 워크시트 생성
  const worksheetData = [headers, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(worksheetData);

  // 컬럼 너비 자동 조정
  const colWidths = exportColumns.map((col, colIndex) => {
    // 헤더 길이
    let maxLength = headers[colIndex].length;

    // 데이터 중 최대 길이 계산
    rows.forEach(row => {
      const cellValue = String(row[colIndex] ?? "");
      if (cellValue.length > maxLength) {
        maxLength = cellValue.length;
      }
    });

    // 한글은 2바이트로 계산
    const width = Math.min(Math.max(maxLength * 1.5, 10), 50);
    return { wch: width };
  });
  ws["!cols"] = colWidths;

  // 워크북 생성
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, ws, sheetName);

  // 파일명 생성
  const dateStr = includeDateInFileName ? `_${getDateString()}` : "";
  const fullFileName = `${fileName}${dateStr}.xlsx`;

  // 파일 다운로드
  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, fullFileName);
};

/**
 * 다중 시트 엑셀 다운로드
 *
 * @param sheets - 시트 데이터 배열
 * @param fileName - 파일명
 *
 * @example
 * downloadMultiSheetExcel([
 *   { name: "매출", data: salesData, columns: salesColumns },
 *   { name: "매입", data: purchaseData, columns: purchaseColumns },
 * ], "거래현황");
 */
export interface SheetData<T = any> {
  name: string;
  data: T[];
  columns: TableColumn[];
}

/**
 * 엑셀 업로드 양식 다운로드
 * 1행: 타이틀 (노란색 배경)
 * 2행: 컬럼 헤더 (회색 배경, *로 시작하는 필수 필드는 빨간색 *로 표시)
 * 3행~: 데이터 입력 영역
 *
 * @param headers - 헤더 배열 (컬럼명, *로 시작하면 필수 필드로 빨간색 * 표시)
 * @param fileName - 파일명 (확장자 제외)
 * @param sheetName - 시트명
 * @param title - 타이틀 (기본값: 시트명)
 *
 * @example
 * downloadExcelTemplate(
 *   ["*주문일자", "도착시간", "*차량번호", "*기사명"],
 *   "주문일보_양식",
 *   "주문일보"
 * );
 */
export const downloadExcelTemplate = (
  headers: string[],
  fileName: string = "양식",
  sheetName: string = "Sheet1",
  title?: string
): void => {
  // 오늘 날짜 포맷
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  const titleText = title || sheetName;
  const titleRow = [`${titleText} 등록 ${year}년 ${month}월 ${day}일`];

  // 빈 셀로 타이틀 행 채우기 (병합을 위해)
  while (titleRow.length < headers.length) {
    titleRow.push("");
  }

  // 워크시트 데이터: 1행 타이틀, 2행 헤더
  const worksheetData = [titleRow, headers];
  const ws = XLSXStyle.utils.aoa_to_sheet(worksheetData);

  // 타이틀 행 병합 (A1부터 마지막 컬럼까지)
  const lastCol = String.fromCharCode(64 + headers.length); // A=65
  const lastColLetter = headers.length <= 26
    ? String.fromCharCode(64 + headers.length)
    : `A${String.fromCharCode(64 + headers.length - 26)}`;

  ws["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } } // 1행 전체 병합
  ];

  // 타이틀 행 스타일 (노란색 배경)
  const titleStyle = {
    fill: { fgColor: { rgb: "FFFF00" } }, // 노란색
    font: { bold: true, sz: 14 },
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } },
    },
  };

  // 헤더 행 스타일 (회색 배경)
  const headerStyle = {
    fill: { fgColor: { rgb: "D9D9D9" } }, // 회색
    font: { bold: true, sz: 11 },
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } },
    },
  };

  // 필수 필드 헤더 스타일 (회색 배경 + 빨간색 글자)
  const requiredHeaderStyle = {
    fill: { fgColor: { rgb: "D9D9D9" } }, // 회색
    font: { bold: true, sz: 11, color: { rgb: "FF0000" } }, // 빨간색 글자
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } },
    },
  };

  // 타이틀 행(1행)에 스타일 적용
  for (let c = 0; c < headers.length; c++) {
    const cellRef = XLSXStyle.utils.encode_cell({ r: 0, c });
    if (!ws[cellRef]) ws[cellRef] = { v: "", t: "s" };
    ws[cellRef].s = titleStyle;
  }

  // 헤더 행(2행)에 스타일 적용 (*로 시작하면 빨간색)
  for (let c = 0; c < headers.length; c++) {
    const cellRef = XLSXStyle.utils.encode_cell({ r: 1, c });
    const header = headers[c];
    const isRequired = header.startsWith("*");

    if (!ws[cellRef]) ws[cellRef] = { v: header, t: "s" };
    ws[cellRef].s = isRequired ? requiredHeaderStyle : headerStyle;
  }

  // 컬럼 너비 설정 (헤더 길이 기준)
  const colWidths = headers.map(header => {
    const width = Math.min(Math.max(header.length * 2, 12), 30);
    return { wch: width };
  });
  ws["!cols"] = colWidths;

  // 행 높이 설정
  ws["!rows"] = [
    { hpt: 30 }, // 1행 타이틀
    { hpt: 25 }, // 2행 헤더
  ];

  // 워크북 생성
  const workbook = XLSXStyle.utils.book_new();
  XLSXStyle.utils.book_append_sheet(workbook, ws, sheetName);

  // 파일 다운로드
  const fullFileName = `${fileName}.xlsx`;
  const excelBuffer = XLSXStyle.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, fullFileName);
};

export const downloadMultiSheetExcel = <T extends Record<string, any>>(
  sheets: SheetData<T>[],
  fileName: string = "export",
  includeDateInFileName: boolean = true
): void => {
  const workbook = XLSX.utils.book_new();

  sheets.forEach(sheet => {
    const exportColumns = sheet.columns.filter(
      col => !col.key.startsWith("__") && col.key !== "no"
    );

    const headers = exportColumns.map(col => getExcelHeader(col, true));

    const rows = sheet.data
      .filter(row => {
        if ("__rowType" in row && row.__rowType === "summary") {
          return false;
        }
        return true;
      })
      .map((row, rowIndex) =>
        exportColumns.map(col =>
          getExcelValue(row[col.key], row, rowIndex, col)
        )
      );

    const worksheetData = [headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);

    // 컬럼 너비 자동 조정
    const colWidths = exportColumns.map((_, colIndex) => {
      let maxLength = headers[colIndex].length;
      rows.forEach(row => {
        const cellValue = String(row[colIndex] ?? "");
        if (cellValue.length > maxLength) {
          maxLength = cellValue.length;
        }
      });
      return { wch: Math.min(Math.max(maxLength * 1.5, 10), 50) };
    });
    ws["!cols"] = colWidths;

    XLSX.utils.book_append_sheet(workbook, ws, sheet.name);
  });

  const dateStr = includeDateInFileName ? `_${getDateString()}` : "";
  const fullFileName = `${fileName}${dateStr}.xlsx`;

  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, fullFileName);
};
